"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RichEditor = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const prop_types_1 = __importDefault(require("prop-types"));
const react_jss_1 = __importDefault(require("react-jss"));
const draft_js_1 = require("draft-js");
const editor_1 = require("../../utils/editor");
const controlPanel_1 = __importDefault(require("./controlPanel"));
const link_1 = __importDefault(require("./controlPanel/link"));
const urlInput_1 = __importDefault(require("./controlPanel/urlInput"));
const richEditor_style_1 = __importDefault(require("./richEditor.style"));
/**
 * We need to add a decorator to add our own style to links
 */
const decorator = new draft_js_1.CompositeDecorator([
    {
        strategy: editor_1.findLinkEntities,
        component: link_1.default
    }
]);
// function to initialize the editor state from an html string
const createStateFromHTML = html => {
    if (html === '')
        return draft_js_1.EditorState.createEmpty(decorator);
    // non null string
    const blocksFromHTML = draft_js_1.convertFromHTML(html);
    const content = draft_js_1.ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
    return draft_js_1.EditorState.createWithContent(content, decorator);
};
const RichEditor = ({ classes, placeholder, disabled, onChange, onFocus, onBlur, initialValue }) => {
    // Initiating the EditorState with link decorator
    const [editorState, setEditorState] = react_1.useState(() => createStateFromHTML(initialValue));
    // Focus applied to the editor and control panel buttons.
    const [hasFocus, setHasFocus] = react_1.useState(false);
    // Url State
    const [showURLInput, setShowURLInput] = react_1.useState(false);
    const [urlValue, setUrlValue] = react_1.useState('');
    const [validUrl, setValidUrl] = react_1.useState(true);
    const [isLinkButtonActive, setLinkButtonActive] = react_1.useState(false);
    const editorRef = react_1.useRef(null);
    const handleFocus = e => {
        onFocus(e);
        editorRef.current.focus();
        setHasFocus(true);
    };
    const handleBlur = e => {
        onBlur(e);
        setHasFocus(false);
    };
    const onEditorStateChange = state => {
        setEditorState(state);
        onChange(editor_1.getHTMLString(state));
    };
    /**
     * Handling urlInput and the link button components
     */
    const onUrlInputChange = e => {
        setUrlValue(e.target.value);
        if (editor_1.isValidURL(e.target.value)) {
            setValidUrl(true);
        }
        else {
            setValidUrl(false);
        }
    };
    const promptForLink = e => {
        e.preventDefault();
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            setShowURLInput(true);
            setUrlValue('');
        }
        setLinkButtonActive(true);
    };
    const confirmLink = e => {
        if (validUrl) {
            e.preventDefault();
            const contentState = editorState.getCurrentContent();
            const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', { target: '_blank', url: urlValue });
            const newState = draft_js_1.RichUtils.toggleLink(editorState, editorState.getSelection(), contentStateWithEntity.getLastCreatedEntityKey());
            onEditorStateChange(newState);
            setShowURLInput(false);
            setLinkButtonActive(false);
        }
        else {
            // This is a placeholder until the error handling has been designed
            console.error('not a valid url');
        }
    };
    // On return key action
    const onLinkInputKeyDown = e => {
        if (e.which === 13) {
            confirmLink(e);
        }
    };
    const removeLink = () => {
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            const newState = draft_js_1.RichUtils.toggleLink(editorState, selection, null);
            onEditorStateChange(newState);
        }
        setLinkButtonActive(false);
    };
    const handleCollapse = () => {
        setShowURLInput(false);
        setLinkButtonActive(false);
    };
    /**
     * e.g: Handles block style such as header-one & bullet points
     */
    const toggleBlockType = blockType => {
        onEditorStateChange(draft_js_1.RichUtils.toggleBlockType(editorState, blockType));
    };
    /**
     * e.g: Handles inline styles such as bold
     */
    const toggleInlineStyle = inlineStyle => {
        onEditorStateChange(draft_js_1.RichUtils.toggleInlineStyle(editorState, inlineStyle));
    };
    /**
     * Handles any inline styles **key commands** such as italics, bold, ...
     */
    const handleKeyCommand = cmd => {
        const newState = draft_js_1.RichUtils.handleKeyCommand(editorState, cmd);
        if (newState) {
            onEditorStateChange(newState);
            return 'handled';
        }
        return 'not-handled';
    };
    /**
     * If the user changes block type before entering any text (e.g unordered-list),
     * we hide the placeholder.
     */
    let hidePlaceholder = false;
    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
        if (contentState.getBlockMap().first().getType() !== 'unstyled')
            hidePlaceholder = true;
    }
    const rootProps = {
        className: classes.root,
        'data-has-focus': hasFocus || showURLInput,
        'data-is-disabled': disabled,
        'data-is-placeholder-hidden': hidePlaceholder,
        'data-cy': 'atomic-rich-editor'
    };
    return (jsx_runtime_1.jsxs("div", Object.assign({ className: classes.editorWrapper }, { children: [jsx_runtime_1.jsxs("div", Object.assign({ onClick: handleFocus, onBlur: handleBlur }, { children: [jsx_runtime_1.jsx(controlPanel_1.default, { editorState: editorState, onToggleBlockType: toggleBlockType, onToggleInlineType: toggleInlineStyle, promptForLink: promptForLink, removeLink: removeLink, disabled: disabled, isLinkButtonActive: isLinkButtonActive }, void 0),
                    jsx_runtime_1.jsx("div", Object.assign({}, rootProps, { children: jsx_runtime_1.jsx(draft_js_1.Editor, { blockStyleFn: editor_1.getBlockStyle, editorState: editorState, onChange: onEditorStateChange, placeholder: placeholder, spellCheck: true, readOnly: disabled, handleKeyCommand: handleKeyCommand, ref: editorRef }, void 0) }), void 0)] }), void 0),
            showURLInput && (jsx_runtime_1.jsx(urlInput_1.default, { confirmLink: confirmLink, onLinkInputKeyDown: onLinkInputKeyDown, urlInputChange: onUrlInputChange, value: urlValue, handleCollapse: handleCollapse, validUrl: validUrl }, void 0))] }), void 0));
};
exports.RichEditor = RichEditor;
exports.RichEditor.propTypes = {
    classes: prop_types_1.default.object.isRequired,
    placeholder: prop_types_1.default.string,
    disabled: prop_types_1.default.bool,
    onChange: prop_types_1.default.func,
    onFocus: prop_types_1.default.func,
    onBlur: prop_types_1.default.func,
    initialValue: prop_types_1.default.string
};
exports.RichEditor.defaultProps = {
    placeholder: '',
    disabled: false,
    onChange: () => { },
    onFocus: () => { },
    onBlur: () => { },
    initialValue: ''
};
exports.default = react_jss_1.default(richEditor_style_1.default)(exports.RichEditor);
//# sourceMappingURL=index.js.map